import type {IDateRangeQueryParam, ITodQueryParam} from '@shared-schemas/metrics-io'

import moment from 'moment'
import {isEqual} from 'lodash'
import dayjs from 'dayjs'
import dayjs_customParseFormat from 'dayjs/plugin/customParseFormat.js'
import jsonURL      from 'json-url'
import router       from '@app/router'
import {formatDateRangeText} from '@app/helpers/formatting'
import client from '@shared/utils/client-rest'
import { formatTODText } from '@shared-utils/formatting'


import { ONE_YEAR_CRASH_DATE } from '@shared/constants'
import { getDatesInRange, removeDuplicatesDate } from '@app/helpers/utils';
import Holidays from 'date-holidays';

const hd = new Holidays("US");
const codec         = jsonURL('lzw')

dayjs.extend(dayjs_customParseFormat)

export const MINIMUM_PERIOD_DAYS = 1 // Currently not 1 to avoid empty ATTs over weekends, etc
export const MAXIMUM_PERIOD_DAYS = 90
export const DEFAULT_PERIOD_DAYS = 28

export const DEFAULT_DATE_RANGE_LONG_TERM_YEARS = 3







const CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';

// Converts a number to a base-62 string
function toBase62(num) {
    if (num === 0) return '0';
    let result = '';
    while (num > 0) {
        result = CHARS[num % 62] + result;
        num = Math.floor(num / 62);
    }
    return result;
}

// Converts a base-62 string back to a number
function fromBase62(str) {
    if(!str) return
    return str.split('').reduce((acc, char) => acc * 62 + CHARS.indexOf(char), 0);
}

// Encodes the timezone, replacing '/' with '_'
function encodeTimezone(timezone) {
    return timezone.replace(/\//g, '_');
}

// Decodes the timezone, replacing the first '_' back to '/'
function decodeTimezone(encodedTimezone) {
    if(!encodedTimezone) return
    return encodedTimezone.replace('_', '/');
}

// Helper to convert HH:MM time to minutes from midnight
function toMinutes(timeStr) {
    const time_match = String(timeStr ?? '').match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/)
    if (!time_match) throw new Error(`Invalid time string: '${timeStr}'.`)

    const [, hour_str, minute_str, second_str = '0'] = time_match
    const hour = Number(hour_str)
    const minute = Number(minute_str)
    const second = Number(second_str)

    if (
        !Number.isInteger(hour) ||
        !Number.isInteger(minute) ||
        !Number.isInteger(second) ||
        hour < 0 ||
        hour > 23 ||
        minute < 0 ||
        minute > 59 ||
        second < 0 ||
        second > 59
    ) {
        throw new Error(`Invalid time string: '${timeStr}'.`)
    }

    let minutes = hour * 60 + minute + Math.round(second / 60);
    if(minutes === 1440) minutes = 0
    return minutes
}

// Helper to convert minutes from midnight back to HH:MM format
function fromMinutes(minutes) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

function normalizeBoolean(value) {
    if (typeof value === 'boolean') return value
    if (value === 'true') return true
    if (value === 'false') return false
    if (value === '1' || value === 1) return true
    if (value === '0' || value === 0) return false
    return null
}

export function normalizeTodForAttParams(tod) {
    if (!tod) return null

    const time_of_day_data = tod?.time_of_day_data || {}
    const days = time_of_day_data.days || tod?.days || {}
    const normalized = {
        ...tod,
        id: tod.id ?? tod.tod_id ?? tod.time_of_day_id,
        name: tod.name ?? tod.tod_name,
        has_monday: normalizeBoolean(tod.has_monday ?? tod.tod_has_monday ?? days.has_monday ?? days.monday),
        has_tuesday: normalizeBoolean(tod.has_tuesday ?? tod.tod_has_tuesday ?? days.has_tuesday ?? days.tuesday),
        has_wednesday: normalizeBoolean(tod.has_wednesday ?? tod.tod_has_wednesday ?? days.has_wednesday ?? days.wednesday),
        has_thursday: normalizeBoolean(tod.has_thursday ?? tod.tod_has_thursday ?? days.has_thursday ?? days.thursday),
        has_friday: normalizeBoolean(tod.has_friday ?? tod.tod_has_friday ?? days.has_friday ?? days.friday),
        has_saturday: normalizeBoolean(tod.has_saturday ?? tod.tod_has_saturday ?? days.has_saturday ?? days.saturday),
        has_sunday: normalizeBoolean(tod.has_sunday ?? tod.tod_has_sunday ?? days.has_sunday ?? days.sunday),
        local_start_time: tod.local_start_time ?? tod.tod_local_start_time ?? tod.start_time ?? time_of_day_data.local_start_time,
        local_end_time: tod.local_end_time ?? tod.tod_local_end_time ?? tod.end_time ?? time_of_day_data.local_end_time,
        timezone_iana: tod.timezone_iana ?? tod.tod_timezone_iana ?? time_of_day_data.timezone_iana,
    }

    if (
        !normalized.local_start_time ||
        !normalized.local_end_time ||
        !normalized.timezone_iana ||
        [
            normalized.has_monday,
            normalized.has_tuesday,
            normalized.has_wednesday,
            normalized.has_thursday,
            normalized.has_friday,
            normalized.has_saturday,
            normalized.has_sunday,
        ].some(value => value == null)
    ) {
        return null
    }

    return normalized
}

export function normalizeTodsForAttParams(tods) {
    const tod_list = Array.isArray(tods) ? tods : (tods ? [tods] : [])
    return tod_list
        .map(normalizeTodForAttParams)
        .filter(Boolean)
}

export type IEncodedTODQueryParam = string & {__brand: 'enc_tod_query_param'}
export type IEncodedDateRangeQueryParam = string & {__brand: 'enc_date_range_query_param'}

export function encodeTODParams(params): IEncodedTODQueryParam {
    const daysBitmask = parseInt([
        params.has_monday, params.has_tuesday, params.has_wednesday,
        params.has_thursday, params.has_friday, params.has_saturday, params.has_sunday
    ].map(hasDay => hasDay ? '1' : '0').join(''), 2);
    const daysEncoded = toBase62(daysBitmask);

    // Encode times to base-62. If start time and end time are the same, we consider it as "all day"
    const startTimeEncoded = toBase62(toMinutes(params.local_start_time));
    const endTimeEncoded = toBase62(toMinutes(params.local_end_time));

    // Encode the timezone, replacing '/' with '_'
    const timezoneEncoded = encodeTimezone(params.timezone_iana);

    // Compile the encoded string with delimiters
    const encoded = `${daysEncoded}.${startTimeEncoded}.${endTimeEncoded}.${timezoneEncoded}`;
    return encoded as IEncodedTODQueryParam;
}

export function decodeTODParams(encoded): ITodQueryParam | {} {

    if(!encoded) return {}

    const parts = encoded.split('.');
    const daysBitmask = fromBase62(parts[0]);
    const daysBinary = daysBitmask.toString(2).padStart(7, '0');
    const timezone = decodeTimezone(parts[3]);

    return {
        has_monday: daysBinary.charAt(0) === '1',
        has_tuesday: daysBinary.charAt(1) === '1',
        has_wednesday: daysBinary.charAt(2) === '1',
        has_thursday: daysBinary.charAt(3) === '1',
        has_friday: daysBinary.charAt(4) === '1',
        has_saturday: daysBinary.charAt(5) === '1',
        has_sunday: daysBinary.charAt(6) === '1',
        local_start_time: fromMinutes(fromBase62(parts[1])),
        local_end_time: fromMinutes(fromBase62(parts[2])),
        timezone_iana: timezone
    };
}


export function encodeDateRange(params): IEncodedDateRangeQueryParam {

    const startDate = dayjs(params.start_date);
    const endDate = dayjs(params.end_date);
    let encoded = toBase62(startDate.format('YYMMDD')) + '.'; // Encode start date
    encoded += toBase62(endDate.diff(startDate, 'days')) + '.'; // Difference to end date

    // Sort and encode excluded dates using delta-delta compression, with fixed-length encoding
    const excludedDates = params.excluded_dates.map(date => dayjs(date)).sort((a, b) => a.diff(b));
    let lastExcludedDate = startDate; // Start from the start date
    const excludedEncoded = excludedDates.map(date => {
        const diff = date.diff(lastExcludedDate, 'days');
        lastExcludedDate = date; // Update for next iteration
        return toBase62(diff).padStart(2, '0'); // Ensure fixed length
    }).join('');

    encoded += excludedEncoded + '.'; // Append encoded excluded dates

	encoded += toBase62(params.exclude_holidays ? 1 : 0) + '.'

	// Encode the timezone, replacing '/' with '_'
	const timezoneEncoded = encodeTimezone(params.timezone_iana);
    encoded += timezoneEncoded; // Append encoded timezone

    return encoded as IEncodedDateRangeQueryParam;
}

export function decodeDateRange(encoded): IDateRangeQueryParam | {} {

    if(!encoded) return {}

    const parts = encoded.split('.');
    const startDate = dayjs(fromBase62(parts[0]).toString(), 'YYMMDD');
    const endDateDiff = fromBase62(parts[1]);
    const endDate = startDate.add(endDateDiff, 'days');

    let lastDate = startDate;
    const excludedDates = [];
    for (let i = 0; i < parts[2].length; i += 2) {
        const diff = fromBase62(parts[2].substring(i, i + 2));
        const excludedDate = lastDate.add(diff, 'days');
        excludedDates.push(excludedDate.format('YYYY-MM-DD'));
        lastDate = excludedDate; // Update last date for next delta
    }

	const exclude_holidays = fromBase62(parts[3]) === 1

	const timezone = decodeTimezone(parts[4]);

    return {
        start_date: startDate.format('YYYY-MM-DD'),
        end_date: endDate.format('YYYY-MM-DD'),
		exclude_holidays: exclude_holidays,
        excluded_dates: excludedDates,
        period_days: endDate.diff(startDate, 'days'),
		timezone_iana: timezone,
    };
}


// Rewrites the `tod`, `dst`, and `dlt` URL params so their `timezone_iana` matches
// the entity's (network) timezone. Guards against the browser-timezone fallback
// (`dayjs.tz.guess()`) getting baked into the ATT params before the real network
// timezone is known. Call this once specs have resolved and a concrete network
// `timezone_iana` is available (NOT from the view, where the guess fallback lives).
// No-op when nothing needs changing, so it converges after a single navigate.
export function syncAttParamsTimezone(timezone_iana) {

	if (!timezone_iana) return

	const current_route = router.currentRoute()
	const { tod, dst, dlt } = current_route?.query || {}

	// Compare through encodeTimezone so multi-slash zones (e.g. America/Indiana/Indianapolis),
	// which decodeTimezone can't fully round-trip, still converge instead of re-navigating forever.
	const target = encodeTimezone(timezone_iana)
	const differs = (decoded_tz) => decoded_tz && encodeTimezone(decoded_tz) !== target

	const query = {}

	if (tod) {
		const decoded = decodeTODParams(tod)
		if (differs(decoded?.timezone_iana)) {
			query.tod = encodeTODParams({ ...decoded, timezone_iana })
		}
	}

	if (dst) {
		const decoded = decodeDateRange(dst)
		if (differs(decoded?.timezone_iana)) {
			query.dst = encodeDateRange({ ...decoded, timezone_iana })
		}
	}

	if (dlt) {
		const decoded = decodeDateRange(dlt)
		if (differs(decoded?.timezone_iana)) {
			query.dlt = encodeDateRange({ ...decoded, timezone_iana })
		}
	}

	if (Object.keys(query).length === 0) return

	current_route.navigate({ query, options: { preserve: true, replace: true } })
}


// Returns the TOD list to feed into the navbar TOD picker for any level of the
// mobility hierarchy (roadway / network / subregion / region). Picks the most
// specific level available. At the region level, suppresses the picker when
// subregions exist for the customer — those customers are expected to drill
// into a subregion before selecting a TOD. Returns null when the picker
// should be hidden (Navbar already treats falsy `tods` as "don't render").
export function getMobilityTODs({ region, subregion, network } = {}) {
    if (network?.tods) return network.tods
    if (subregion?.tods) return subregion.tods
    if (region?.subregions?.length > 0) return null
    return region?.tods ?? null
}


export function getTOD(tods){

    const current_route = router.currentRoute()
    const current_tod_key = current_route?.query?.tod

    if(!current_tod_key) return {name: null}

    let current_tod

    if(tods){
        tods.forEach(tod => {
            if(!tod.key) tod.key = encodeTODParams(tod)
            if(tod.key == current_tod_key) current_tod = tod
        })
    }

    if(!current_tod){
        current_tod = decodeTODParams(current_tod_key)
    }

	current_tod.name = formatTODText(current_tod) // For standardized display

    return current_tod

}

export function getDateRange(options){

	const is_long_term = options?.is_long_term

	const current_route = router.currentRoute()
	const dr = (is_long_term) ? current_route?.query?.dlt : current_route?.query?.dst

	if(!dr) return {name: null}

	const date_range = decodeDateRange(dr)
	date_range.name = formatDateRangeText(date_range)

	return date_range

}


// Assumes tods are decoded
// LEGACY: Refer to compareMinimalTODs()
export function sortTODs(tods) {

	if (!tods) return []

	if (!Array.isArray(tods)) return [tods]

	return [

		// All Periods
		...tods.filter((tod) => tod.name === "All Periods"),

		// Weekdays and Weekends
		...tods.filter(tod => tod.name?.toLowerCase().startsWith("weekend") || tod.name?.toLowerCase().startsWith("weekday"))
			.toSorted((a, b) => {
				if (
					(a.name?.toLowerCase().startsWith("weekend") && b.name?.toLowerCase().startsWith("weekend"))
					|| (a.name?.toLowerCase().startsWith("weekday") && b.name?.toLowerCase().startsWith("weekday"))
				) {
					const a_start_time = a?.time_of_day_data?.local_start_time ?? a.local_start_time
					const b_start_time = b?.time_of_day_data?.local_start_time ?? b.local_start_time
					return a_start_time >= b_start_time ? 1 : -1
				}
				return a.name >= b.name ? 1 : -1
			}),

		// Days of Week
		...tods.filter((tod) =>
			!(
				tod.name === "All Periods"
				|| tod.name?.toLowerCase().startsWith("weekend")
				|| tod.name?.toLowerCase().startsWith("weekday")
				|| tod.name === "Custom Period"
			)
		)
			.toSorted((a, b) => {
				let a_day = 0
				let b_day = 0
				let days_of_week = [
					"mon",
					"tue",
					"wed",
					"thu",
					"fri",
					"sat",
					"sun",
				]
				days_of_week.forEach((day, idx) => {
					if (a.name?.toLowerCase().startsWith(day))
						a_day = idx
					if (b.name?.toLowerCase().startsWith(day))
						b_day = idx
				})
				if (a_day == b_day) {
					const a_start_time = a?.time_of_day_data?.local_start_time ?? a.local_start_time
					const b_start_time = b?.time_of_day_data?.local_start_time ?? b.local_start_time
					return a_start_time >= b_start_time ? 1 : -1
				}
				return a_day >= b_day ? 1 : -1
			}),
	]

}

export const isSameTodDescription = (a, b) => {

	if (a?.time_of_day_id && b?.time_of_day_id) {
		return (
			isEqual(a?.time_of_day_data?.days, b?.time_of_day_data?.days)
			&& isEqual(a?.time_of_day_data?.months ?? null, b?.time_of_day_data?.months ?? null)
			&& isEqual(a?.time_of_day_data?.days_of_month ?? null, b?.time_of_day_data?.days_of_month ?? null)
			&& a?.time_of_day_data?.local_start_time === b?.time_of_day_data?.local_start_time
			&& a?.time_of_day_data?.local_end_time === b?.time_of_day_data?.local_end_time
		)
	}
	else if (
		(a?.time_of_day_id && !b?.time_of_day_id)
		|| (!a?.time_of_day_id && b?.time_of_day_id)
	) {

		const v1_tod = a?.time_of_day_id ? b : a
		const v2_tod = a?.time_of_day_id ? a : b

		return (
			isEqual(v2_tod?.time_of_day_data?.days, {
				has_monday: v1_tod?.has_monday,
				has_tuesday: v1_tod?.has_tuesday,
				has_wednesday: v1_tod?.has_wednesday,
				has_thursday: v1_tod?.has_thursday,
				has_friday: v1_tod?.has_friday,
				has_saturday: v1_tod?.has_saturday,
				has_sunday: v1_tod?.has_sunday,
			})
			&& v2_tod?.time_of_day_data?.local_start_time === v1_tod?.local_start_time
			&& v2_tod?.time_of_day_data?.local_end_time === v1_tod?.local_end_time
		)
	}
	else {
		return (
			a?.has_monday === b?.has_monday
			&& a?.has_tuesday === b?.has_tuesday
			&& a?.has_wednesday === b?.has_wednesday
			&& a?.has_thursday === b?.has_thursday
			&& a?.has_friday === b?.has_friday
			&& a?.has_saturday === b?.has_saturday
			&& a?.has_sunday === b?.has_sunday
			&& a?.local_start_time === b?.local_start_time
			&& a?.local_end_time === b?.local_end_time
		)
	}

}

const MONTH_KEY_ORDER = ['january','february','march','april','may','june','july','august','september','october','november','december']
const DOM_KEY_ORDER = Array.from({ length: 31 }, (_, i) => String(i + 1))

const allTrueMonths = () => MONTH_KEY_ORDER.reduce((acc, key) => { acc[key] = true; return acc }, {})
const allTrueDoms = () => DOM_KEY_ORDER.reduce((acc, key) => { acc[key] = true; return acc }, {})

export const generateTodDescription = (tod) => {
	const dow_part = [
		(tod?.days?.['monday'] || tod.has_monday) ? 'M' : '',
		(tod?.days?.['tuesday'] || tod.has_tuesday) ? 'Tu' : '',
		(tod?.days?.['wednesday'] || tod.has_wednesday) ? 'W' : '',
		(tod?.days?.['thursday'] || tod.has_thursday) ? 'Th' : '',
		(tod?.days?.['friday'] || tod.has_friday) ? 'F' : '',
		(tod?.days?.['saturday'] || tod.has_saturday) ? 'Sa' : '',
		(tod?.days?.['sunday'] || tod.has_sunday) ? 'Su' : '',
	].join('')

	const months_source = tod?.months
	const months_part = MONTH_KEY_ORDER.map((key) => {
		// Missing months => treat as all-true so legacy descriptions still parse to all-year.
		if (!months_source) return '1'
		return months_source[key] ? '1' : '0'
	}).join('')

	const doms_source = tod?.days_of_month
	const doms_part = DOM_KEY_ORDER.map((key) => {
		if (!doms_source) return '1'
		return doms_source[key] ? '1' : '0'
	}).join('')

	const time_part = `${tod.local_start_time}_${tod.local_end_time}`

	return `${dow_part}_${months_part}_${doms_part}_${time_part}`
}

export const generateMinimalTODFromTODDescription = (tod_desc) => {

	const segments = tod_desc.split('_')
	// Legacy formats supported:
	//   3 segments: dow_HH:mm_HH:mm (no months, no doms)
	//   4 segments: dow_<12-bit months>_HH:mm_HH:mm (no doms)
	//   5 segments: dow_<12-bit months>_<31-bit doms>_HH:mm_HH:mm (full)
	const has_months_segment = segments.length >= 4 && /^[01]{12}$/.test(segments[1])
	const has_doms_segment = segments.length >= 5 && /^[01]{31}$/.test(segments[2])

	const dow_part = segments[0]
	const months_part = has_months_segment ? segments[1] : null
	const doms_part = has_doms_segment ? segments[2] : null

	let local_start_time
	let local_end_time
	if (has_doms_segment) {
		local_start_time = segments[3]
		local_end_time = segments[4]
	} else if (has_months_segment) {
		local_start_time = segments[2]
		local_end_time = segments[3]
	} else {
		local_start_time = segments[1]
		local_end_time = segments[2]
	}

	const months = months_part
		? MONTH_KEY_ORDER.reduce((acc, key, idx) => {
			acc[key] = months_part.charAt(idx) === '1'
			return acc
		}, {})
		: allTrueMonths()

	const days_of_month = doms_part
		? DOM_KEY_ORDER.reduce((acc, key, idx) => {
			acc[key] = doms_part.charAt(idx) === '1'
			return acc
		}, {})
		: allTrueDoms()

	return {
		days: {
			monday: dow_part.includes('M'),
			tuesday: dow_part.includes('Tu'),
			wednesday: dow_part.includes('W'),
			thursday: dow_part.includes('Th'),
			friday: dow_part.includes('F'),
			saturday: dow_part.includes('Sa'),
			sunday: dow_part.includes('Su'),
		},
		months,
		days_of_month,
		local_start_time,
		local_end_time,
	}
}

export const getPrettyLabelForTodDescription = (tod_desc) => {
	return formatTODText(
		generateMinimalTODFromTODDescription(tod_desc)
	)
}

// TOD name presets offered in the optimus TOD name dropdown (config + job-request modal).
export const TOD_NAME_PRESETS = ['AM Peak', 'Midday', 'PM Peak', 'Evening', 'Late Evening', 'Early Morning', 'Daytime']
export const NETWORK_TOD_NAME_PRESETS = [
	'Weekday Early Morning',
	'Weekday AM Peak',
	'Weekday Midday',
	'Weekday Daytime',
	'Weekday PM Peak',
	'Weekday Evening',
	'Weekday Late Evening',
	'Weekend Early Morning',
	'Weekend AM Peak',
	'Weekend Midday',
	'Weekend Daytime',
	'Weekend PM Peak',
	'Weekend Evening',
	'Weekend Late Evening',
]

// WARNING: Currently does not support legacy TODs
export function compareMinimalTODs(a, b) {

	const is_daily_a = a.days.monday && a.days.tuesday && a.days.wednesday && a.days.thursday && a.days.friday && a.days.saturday && a.days.sunday
	const is_daily_b = b.days.monday && b.days.tuesday && b.days.wednesday && b.days.thursday && b.days.friday && b.days.saturday && b.days.sunday
	if (is_daily_a && !is_daily_b) {
		return -1
	}
	else if (is_daily_b && !is_daily_a) {
		return 1
	}

	const is_weekdays_a = a.days.monday && a.days.tuesday && a.days.wednesday && a.days.thursday && a.days.friday && !a.days.saturday && !a.days.sunday
	const is_weekdays_b = b.days.monday && b.days.tuesday && b.days.wednesday && b.days.thursday && b.days.friday && !b.days.saturday && !b.days.sunday
	if (is_weekdays_a && !is_weekdays_b) {
		return -1
	}
	else if (is_weekdays_b && !is_weekdays_a) {
		return 1
	}

	const is_weekends_a = !a.days.monday && !a.days.tuesday && !a.days.wednesday && !a.days.thursday && !a.days.friday && a.days.saturday && a.days.sunday
	const is_weekends_b = !b.days.monday && !b.days.tuesday && !b.days.wednesday && !b.days.thursday && !b.days.friday && b.days.saturday && b.days.sunday
	if (is_weekends_a && !is_weekends_b) {
		return -1
	}
	else if (is_weekends_b && !is_weekends_a) {
		return 1
	}

	const days_bitstr_a = [a.days.monday, a.days.tuesday, a.days.wednesday, a.days.thursday, a.days.friday, a.days.saturday, a.days.sunday].map(x => x ? '1' : '0').join('')
	const days_bitstr_b = [b.days.monday, b.days.tuesday, b.days.wednesday, b.days.thursday, b.days.friday, b.days.saturday, b.days.sunday].map(x => x ? '1' : '0').join('')
	// Note: >, not <
	if (days_bitstr_a > days_bitstr_b) {
		return -1
	}
	else if (days_bitstr_b > days_bitstr_a) {
		return 1
	}

	// Missing months means "all year" (legacy data), so absent or fully-empty maps both produce all-1s.
	const monthsToBitstr = (months) => {
		if (!months) return '111111111111'
		const bits = MONTH_KEY_ORDER.map((k) => months[k] ? '1' : '0').join('')
		return bits === '000000000000' ? '111111111111' : bits
	}
	const months_bitstr_a = monthsToBitstr(a.months)
	const months_bitstr_b = monthsToBitstr(b.months)
	if (months_bitstr_a > months_bitstr_b) {
		return -1
	}
	else if (months_bitstr_b > months_bitstr_a) {
		return 1
	}

	// Same convention for days_of_month: missing/all-false => all 31 days.
	const domsToBitstr = (doms) => {
		const all_ones = '1'.repeat(31)
		if (!doms) return all_ones
		const bits = DOM_KEY_ORDER.map((k) => doms[k] ? '1' : '0').join('')
		return bits === '0'.repeat(31) ? all_ones : bits
	}
	const doms_bitstr_a = domsToBitstr(a.days_of_month)
	const doms_bitstr_b = domsToBitstr(b.days_of_month)
	if (doms_bitstr_a > doms_bitstr_b) {
		return -1
	}
	else if (doms_bitstr_b > doms_bitstr_a) {
		return 1
	}

	if (a.local_start_time < b.local_start_time) {
		return -1
	}
	else if (b.local_start_time < a.local_start_time) {
		return 1
	}

	if (a.local_end_time < b.local_end_time) {
		return -1
	}
	else if (b.local_end_time < a.local_end_time) {
		return 1
	}

	return 1
}



export const getCompressedQueryParam = async url_key => {

    // console.log('GETTING', url_key)

    const current_route = router.currentRoute()
    const customer_id = current_route?.params?.customer_id
    const lt_years = ONE_YEAR_CRASH_DATE.includes(customer_id) ? 5 : DEFAULT_DATE_RANGE_LONG_TERM_YEARS
    const lt_days = lt_years * 365



    if(!url_key.includes('litmus')){


        const params = {}
        params.tod_params = decodeTODParams(current_route?.query?.tod)
        params.date_range = decodeDateRange(current_route?.query?.dst)
        params.date_range_long_term = (current_route?.query?.dlt) ? decodeDateRange(current_route?.query?.dlt) : {start_date: dayjs().subtract(lt_years, 'year').format('YYYY-MM-DD'), end_date: dayjs().format('YYYY-MM-DD'), period_days: lt_days}

		// TEMP - Backwards compatibility until end-to-end update to new ATT Params style
        if(url_key.includes('_lt_')){
            params.all_time = true
            params.period_end_date = params.date_range_long_term.end_date
            params.period_days = params.date_range_long_term.period_days
			params.start_time = params.tod_params.local_start_time
            params.end_time = params.tod_params.local_end_time
            params.timezone_iana = params.tod_params.timezone_iana
			params.has_monday = params.tod_params.has_monday
            params.has_tuesday = params.tod_params.has_tuesday
            params.has_wednesday = params.tod_params.has_wednesday
            params.has_thursday = params.tod_params.has_thursday
            params.has_friday = params.tod_params.has_friday
            params.has_saturday = params.tod_params.has_saturday
            params.has_sunday = params.tod_params.has_sunday
        }
        else{
            params.period_start = params.date_range.start_date
            params.period_end = params.date_range.end_date
			params.period_days = params.date_range.period_days
            params.start_time = params.tod_params.local_start_time
            params.end_time = params.tod_params.local_end_time
            params.timezone_iana = params.tod_params.timezone_iana
            params.has_monday = params.tod_params.has_monday
            params.has_tuesday = params.tod_params.has_tuesday
            params.has_wednesday = params.tod_params.has_wednesday
            params.has_thursday = params.tod_params.has_thursday
            params.has_friday = params.tod_params.has_friday
            params.has_saturday = params.tod_params.has_saturday
            params.has_sunday = params.tod_params.has_sunday
        }



        return [params, current_route?.query?.tod+current_route?.query?.dst]
    }


    const compressed_params = current_route?.query?.[url_key]

    // console.log('COMPRESSED', compressed_params)

    if(!compressed_params) return [null, null]

    try{
        var params = await codec.decompress(compressed_params)
    }
    catch(e){
        console.warn(e, `Invalid compressed params: '${compressed_params}'`)
        var params = null
    }

    // console.log('DECOMPRESSED', params)


    // console.log('RETURNING', [params, compressed_params])
    // console.trace()
    return [params, compressed_params]

}

// NOTE: When modifying individual att params, prefer this instead of navigate()
//       as it is the current home of syncing logic (across SigOps and Safety Modes, across root_atts and lt_atts).
export const setCompressedQueryParam = async (url_key, params, replace) => {

    const compressed_params = params ? await codec.compress(params) : undefined

    const url_params = {}
    url_params[url_key] = compressed_params

    const current_route = router.currentRoute()

    // ----- HACK START: Sync (existing!!!) att_params across SigOps and Safety modes
    // ----- NOTE: ONLY WORKS FOR 'manual' time_range_strategy params!!!
    if (['root_att', 'lt_att', 'safety_root_att', 'safety_lt_att'].includes(url_key)) {

        if (url_key === 'root_att') {
            // const [lt_params, lt_params_string] = await getCompressedQueryParam('lt_att')
            const [safety_root_params, safety_root_params_string] = await getCompressedQueryParam('safety_root_att')
            const [safety_lt_params, safety_lt_params_string] = await getCompressedQueryParam('safety_lt_att')

            // Sync roots
            if (params.period_days && params.period_start) {
                if (safety_root_params) {
                    delete safety_root_params.period_end
                    safety_root_params.period_days = params.period_days
                    safety_root_params.period_start = params.period_start
                }
            }
            else if (params.period_days && params.period_end) {
                if (safety_root_params) {
                    delete safety_root_params.period_start
                    safety_root_params.period_days = params.period_days
                    safety_root_params.period_end = params.period_end
                }
            }

            // Sync only date across short-term and long-term
            if (params.period_start) {
                if (safety_lt_params) {
                    delete safety_lt_params.period_start_date
                    safety_lt_params.period_end_date = moment.utc(params.period_start).add(params.period_days || 28, 'days').startOf('day').add(12, 'hours').toISOString().slice(0, 10)
                }
            }
            else if (params.period_end) {
                if (safety_lt_params) {
                    delete safety_lt_params.period_start_date
                    safety_lt_params.period_end_date = moment.utc(params.period_end).startOf('day').add(12, 'hours').toISOString().slice(0, 10)
                }
            }

            ['start_time', 'end_time', 'timezone_iana', 'has_monday', 'has_tuesday', 'has_wednesday', 'has_thursday', 'has_friday', 'has_saturday', 'has_sunday'].forEach(key => {
                if(params[key] != null) {
                    // TODO: Translate safety's 'all_time' to sigop's 'all' structure
                    if (safety_root_params) safety_root_params[key] = params[key]
                    if (safety_lt_params)   safety_lt_params[key] = params[key]
                }
            })

            if (safety_root_params) url_params['safety_root_att'] = await codec.compress(safety_root_params)
            if (safety_lt_params)   url_params['safety_lt_att'] = await codec.compress(safety_lt_params)

        }
        else if (url_key === 'lt_att') {
            console.error('Not implemented')
        }
        else if (url_key === 'safety_root_att') {
            const [root_params, root_params_string] = await getCompressedQueryParam('root_att')
            // const [lt_params, lt_params_string] = await getCompressedQueryParam('lt_att')
            const [safety_lt_params, safety_lt_params_string] = await getCompressedQueryParam('safety_lt_att')


            // Sync roots
            if (params.period_days && params.period_start) {
                if (root_params) {
                    delete root_params.period_end
                    root_params.period_days = params.period_days
                    root_params.period_start = params.period_start
                }
            }
            else if (params.period_days && params.period_end) {
                if (root_params) {
                    delete root_params.period_start
                    root_params.period_days = params.period_days
                    root_params.period_end = params.period_end
                }
            }

            // Sync only date across short-term and long-term
            if (params.period_start) {
                if (safety_lt_params) {
                    delete safety_lt_params.period_start_date
                    safety_lt_params.period_end_date = moment.utc(params.period_start).add(params.period_days || 28, 'days').startOf('day').add(12, 'hours').toISOString().slice(0, 10)
                }
            }
            else if (params.period_end) {
                if (safety_lt_params) {
                    delete safety_lt_params.period_start_date
                    safety_lt_params.period_end_date = moment.utc(params.period_end).startOf('day').add(12, 'hours').toISOString().slice(0, 10)
                }
            }

            ['start_time', 'end_time', 'timezone_iana', 'has_monday', 'has_tuesday', 'has_wednesday', 'has_thursday', 'has_friday', 'has_saturday', 'has_sunday'].forEach(key => {
                if(params[key] != null) {
                    // TODO: Translate safety's 'all_time' to sigop's 'all' structure
                    if (root_params) {
                        if (root_params?.network_ids || root_params?.tod_ids) return // Keep SigOps's current "all" or "tod" time_range_strategy if currently in use
                        root_params[key] = params[key]
                    }
                    if (safety_lt_params)   safety_lt_params[key] = params[key]
                }
            })

            if (root_params)        url_params['root_att'] = await codec.compress(root_params)
            if (safety_lt_params)   url_params['safety_lt_att'] = await codec.compress(safety_lt_params)

        }
        else if (url_key === 'safety_lt_att') {
            const [root_params, root_params_string] = await getCompressedQueryParam('root_att')
            // const [lt_params, lt_params_string] = await getCompressedQueryParam('lt_att')
            const [safety_root_params, safety_root_params_string] = await getCompressedQueryParam('safety_root_att')


            // Sync only date with roots
            if (params.period_start_date) {
                if (root_params) {
                    delete root_params.period_end
                    root_params.period_start = moment.utc(params.period_start_date).startOf('day').add(12, 'hours').valueOf()
                }
                if (safety_root_params) {
                    delete safety_root_params.period_end
                    safety_root_params.period_start = moment.utc(params.period_start_date).startOf('day').add(12, 'hours').valueOf()
                }
            }
            else if (params.period_end_date) {
                if (root_params) {
                    delete root_params.period_start
                    root_params.period_end = moment.utc(params.period_end_date).startOf('day').add(12, 'hours').valueOf()
                }
                if (safety_root_params) {
                    delete safety_root_params.period_start
                    safety_root_params.period_end = moment.utc(params.period_end_date).startOf('day').add(12, 'hours').valueOf()
                }
            }

            // Sync long-term params
            // if (params.period_start_date) {
            //   TODO
            // }
            // else if (params.period_end_date) {
            //   TODO
            // }
            // if (params.period_days) {
            //     if (lt_params) lt_params.period_days = params.period_days
            // }


            ['start_time', 'end_time', 'timezone_iana', 'has_monday', 'has_tuesday', 'has_wednesday', 'has_thursday', 'has_friday', 'has_saturday', 'has_sunday'].forEach(key => {
                if(params[key] != null) {
                    // TODO: Translate safety's 'all_time' to sigop's 'all' structure
                    if (root_params) {
                        if (root_params?.network_ids || root_params?.tod_ids) return // Keep SigOps's current "all" or "tod" time_range_strategy if currently in use
                        root_params[key] = params[key]
                    }
                    if (safety_root_params)   safety_root_params[key] = params[key]
                }
            })

            if (root_params)          url_params['root_att'] = await codec.compress(root_params)
            if (safety_root_params)   url_params['safety_root_att'] = await codec.compress(safety_root_params)

        }
    }

    url_params.exclude_holidays = params?.exclude_holidays ?? false

    // ----- HACK END: Sync att_params across SigOps and Safety modes

    current_route.navigate({
        query: url_params,
        options: { preserve: true, replace: replace },
    })

    return [params, compressed_params]
}


export const paramsToForm = (params) => {

	const form = {}

	if(!params) return form

	if(params?.tod_ids?.length > 0){
		form.time_range_strategy = 'tod'
		form.tod_id = params.tod_ids[0]
	}
	else if(params?.network_ids?.length > 0){
        form.time_range_strategy = 'all'
		form.network_id = params?.network_ids?.[0]
	}
	else form.time_range_strategy = 'manual'

	if(params.period_days && params.period_start){
		form.date_range_strategy = 'starting'
		form.reference_date = moment.utc(params.period_start).startOf('day').add(12, 'hours').format('YYYY-MM-DD')
	}
	else if(params.period_start && params.period_end) form.date_range_strategy = 'date_range'
	else{
		form.date_range_strategy = 'ending'
        form.reference_date = moment.utc(params.period_end).startOf('day').add(12, 'hours').format('YYYY-MM-DD')
	}

	Object.assign(form, params)

	delete form.tod_ids
	delete form.network_ids
	delete form.period_start
	delete form.period_end

	return form
}

export const formToParams = form => {

	const params = {}

	if(!form) return params

    params.report_type = form.report_type

	if(form.time_range_strategy === 'tod'){
		params.tod_ids = [form.tod_id]
	}
	else if(form.time_range_strategy === 'all'){
		params.network_ids = [form.network_id]
	}
	else{
		['all_time', 'start_time', 'end_time', 'timezone_iana', 'has_monday', 'has_tuesday', 'has_wednesday', 'has_thursday', 'has_friday', 'has_saturday', 'has_sunday'].forEach(key => {
			if(form[key] != null) params[key] = form[key]
		})
	}


	if(form.date_range_strategy === 'starting'){
		params.period_days = form.period_days
		params.period_start = moment.utc(form.reference_date).startOf('day').add(12, 'hours').valueOf()
	}
	else if(form.date_range_strategy === 'ending'){
		params.period_days = form.period_days
		params.period_end = moment.utc(form.reference_date).startOf('day').add(12, 'hours').valueOf()
	}
	else{
		params.period_start = moment.utc(form.start_date).startOf('day').add(12, 'hours').valueOf()
		params.period_end = moment.utc(form.end_date).startOf('day').add(12, 'hours').valueOf()
	}

    params.exclude_holidays = form?.exclude_holidays ?? false

	return params
}

export const longTermParamsToForm = (params) => {

    let form = {}

    if(!params) return form

    // if (params.all_time) form.time_range_strategy = 'all'
    // else form.time_range_strategy = 'manual'
    form.time_range_strategy = 'manual'

    if (params.period_days && params.period_start_date) {
        form.date_range_strategy = 'starting'
        form.reference_date = moment.utc(params.period_start_date).startOf('day').add(12, 'hours').format('YYYY-MM-DD')
    }
    else if (params.period_days && params.period_end_date) {
        form.date_range_strategy = 'ending'
        form.reference_date = moment.utc(params.period_end_date).startOf('day').add(12, 'hours').format('YYYY-MM-DD')
    }
    else console.warn('Not implemented: date_range_strategy = date_range')

    Object.assign(form, params)

	delete form.tod_ids
	delete form.network_ids
	delete form.period_start
	delete form.period_end
	delete form.period_start_date
	delete form.period_end_date

    return form
}

export const formToLongTermParams = (form) => {

	const params = {}

	if(!form) return params

    // if(form.time_range_strategy === 'all'){
	// 	params.all_time = true
	// }
	// else{
	// 	['start_time', 'end_time', 'timezone_iana', 'has_monday', 'has_tuesday', 'has_wednesday', 'has_thursday', 'has_friday', 'has_saturday', 'has_sunday'].forEach(key => {
	// 		if(form[key] != null) params[key] = form[key]
	// 	})
	// }
    const keys = ['all_time', 'start_time', 'end_time', 'timezone_iana', 'has_monday', 'has_tuesday', 'has_wednesday', 'has_thursday', 'has_friday', 'has_saturday', 'has_sunday']
    keys.forEach(key => {
        if(form[key] != null) params[key] = form[key]
    })

	if (form.date_range_strategy === 'starting') {
		params.period_days = form.period_days
		params.period_start_date = form.reference_date
	}
	else if (form.date_range_strategy === 'ending') {
		params.period_days = form.period_days
		params.period_end_date = form.reference_date
	}
	else console.warn('Not implemented: date_range_strategy = date_range')

	return params
}

export const convertAttParamsToLongTermAttParams = params => {

    if (!params) return params

    return Object.fromEntries(Object.entries(params).map(([k,v]) => {
        if (k === 'period_start') {
            return ['period_start_date', moment.utc(v).startOf('day').add(12, 'hours').toISOString().slice(0, 10)]
        }
        else if (k === 'period_end') {
            return ['period_end_date', moment.utc(v).startOf('day').add(12, 'hours').toISOString().slice(0, 10)]
        }
        return [k,v]
    }))

}

export const convertLongTermAttParamsToAttParams = params => {

    if (!params) return params

    return Object.fromEntries(Object.entries(params).map(([k,v]) => {
        if (k === 'period_start_date') {
            return ['period_start', moment.utc(v).startOf('day').add(12, 'hours').valueOf()]
        }
        else if (k === 'period_end_date') {
            return ['period_end', moment.utc(v).startOf('day').add(12, 'hours').valueOf()]
        }
        return [k,v]
    }))

}


export const getErrors = data => {

	data = data || {}

	const errors = { has_errors: false }

	if( !(['starting', 'ending', 'date_range']).includes(data.date_range_strategy) ) errors['date_range_strategy'] = 'A valid date range strategy is required.'
	if( !(['all', 'tod', 'manual']).includes(data.time_range_strategy) ) errors['time_range_strategy'] = 'A valid time range strategy is required.'

	if( ['starting', 'ending'].includes(data.date_range_strategy) ){
		if(!data.period_days) errors['period_days'] = "The number of days to look back is required."
		if(!data.reference_date) errors['reference_date'] = "The reference date from which to look back is required."

        if(data.period_days < 1) errors['date_range'] = "The start date must begin before the end date."
        if(data.period_days < MINIMUM_PERIOD_DAYS || data.period_days > MAXIMUM_PERIOD_DAYS) errors['period_days'] = `The time interval must be between ${MINIMUM_PERIOD_DAYS} to ${MAXIMUM_PERIOD_DAYS} days.`
	}

	if(data.date_range_strategy === 'date_range'){
		if(!data.start_date) errors['start_date'] = "The date to start the lookback date range is required."
		if(!data.end_date) errors['end_date'] = "The date to end the lookback date range is required."
        if(data.start_date && data.end_date && moment(data.start_date).isSameOrAfter(moment(data.end_date))) errors['date_range'] = "The start date must begin before the end date."
        const interval_days = moment(data.end_date).diff(moment(data.start_date), 'days')
        if(interval_days < 1) errors['date_range'] = "The start date must begin before the end date."
        if(interval_days < MINIMUM_PERIOD_DAYS || interval_days > MAXIMUM_PERIOD_DAYS) errors['date_range'] = `The time interval must be between ${MINIMUM_PERIOD_DAYS} to ${MAXIMUM_PERIOD_DAYS} days.`
	}

	if( data.time_range_strategy === 'tod'){
		if(!data.tod_id) errors['time_range_strategy'] = "A timing plan to analyze is required."
	}

	if( data.time_range_strategy === 'all'){
		// TODO (Refactor): Currently, data.all_time = true is used for All Period queries that are not dependent on network tod plans
        if(!data.all_time && !data.network_id) errors['time_range_strategy'] = "A timing plan to analyze is required."
	}

	if( data.time_range_strategy === 'manual'){
		if(!data.start_time) errors['start_time'] = "The daily start time is required."
		if(!data.end_time) errors['end_time'] = "The daily end time is required."
		if(!data.timezone_iana) errors['timezone_iana'] = "The timezone is required."
		if(!data.has_monday && !data.has_tuesday && !data.has_wednesday && !data.has_thursday && !data.has_friday && !data.has_saturday && !data.has_sunday){
			errors['days'] = "At least one day of the week is required."
		}

        // TEMP PATCH
        // if (data.start_time && data.end_time) {
        //     const _start_time = dayjs().hour(data.start_time.slice(0, 2)).minute(data.start_time.slice(3, 5))
        //     const _end_time = dayjs().hour(data.end_time.slice(0, 2)).minute(data.end_time.slice(3, 5))
        //     if(_start_time.isAfter(_end_time)) errors['time_range'] = "The daily start time must be before the daily end time."
        // }
	}

	if(Object.keys(errors).length > 1) errors.has_errors = true

	return errors
}

export const getBeforeAfterPeriodErrors = (before_period_form_data, after_period_form_data) => {

    before_period_form_data = before_period_form_data || {}
    after_period_form_data = after_period_form_data || {}

	const errors = { has_errors: false, before: {}, after: {} }


    if (before_period_form_data.time_range_strategy && after_period_form_data.time_range_strategy && (before_period_form_data.time_range_strategy !== 'manual' || after_period_form_data.time_range_strategy !== 'manual')) throw new Error('Not Implemented')

    if (!before_period_form_data.period_days || !(before_period_form_data.period_days <= MAXIMUM_PERIOD_DAYS && before_period_form_data.period_days >= MINIMUM_PERIOD_DAYS)) errors.before['period_days'] = `Period Days must be between ${MINIMUM_PERIOD_DAYS} and ${MAXIMUM_PERIOD_DAYS}.`
    if (!after_period_form_data.period_days || !(after_period_form_data.period_days <= MAXIMUM_PERIOD_DAYS && after_period_form_data.period_days >= MINIMUM_PERIOD_DAYS)) errors.after['period_days'] = `Period Days must be between ${MINIMUM_PERIOD_DAYS} and ${MAXIMUM_PERIOD_DAYS}.`


    if (before_period_form_data.date_range_strategy && after_period_form_data.date_range_strategy && (!['starting', 'ending'].includes(before_period_form_data.date_range_strategy) || !['starting', 'ending'].includes(after_period_form_data.date_range_strategy))) throw new Error('Not Implemented')

    const [before_start_date, before_end_date] = (before_period_form_data.date_range_strategy === 'starting') ? [moment(before_period_form_data.reference_date), moment(before_period_form_data.reference_date).add(before_period_form_data.period_days, 'day')]
        : [moment(before_period_form_data.reference_date).subtract(before_period_form_data.period_days, 'day'), moment(before_period_form_data.reference_date)]
    const [after_start_date, after_end_date] = (after_period_form_data.date_range_strategy === 'starting') ? [moment(after_period_form_data.reference_date), moment(after_period_form_data.reference_date).add(after_period_form_data.period_days, 'day')]
        : [moment(after_period_form_data.reference_date).subtract(after_period_form_data.period_days, 'day'), moment(after_period_form_data.reference_date)]

    if (!before_start_date.isSameOrBefore(after_start_date)) errors.before['reference_date'] = errors.after['reference_date'] = 'Before Period start date must be same or before After Period start date.'
    if (!before_end_date.isSameOrBefore(after_end_date)) errors.before['reference_date'] = errors.after['reference_date'] = 'Before Period end date must be same or before After Period end date.'


    if (Object.keys(errors.before).length > 0 || Object.keys(errors.after).length > 0) errors.has_errors = true

    return errors

}


export const getErrorsExclusions = ({
    start_date,
    end_date,
    exclude_holidays,
    data,
  }) => {
    let result = [];
    let excluded_dates_list = [];
    const date_range = getDatesInRange({
      start_date,
      end_date
    });

    if (data?.length) {
      data.map(
        (i) =>
          (excluded_dates_list = [...excluded_dates_list, ...getDatesInRange(i)])
      );
    }

    if (exclude_holidays) {
      const d = [];
      date_range.map((i) => {
        if(hd.isHoliday(new Date(i))?.[0]?.type !== "public") {
            d.push(i);
        }
    });
      result = removeDuplicatesDate(d, excluded_dates_list);
    } else {
      result = removeDuplicatesDate(date_range, excluded_dates_list);
    }

    return !!!result?.length;
  };


const HAS_SEVERE_CRASHES = ['pPYagA9jydDGJ4MJpw1MCA']

// TODO: DEPRECATED
export const createLitmusParams = (customer_id, inputs, start_date, end_date, excluded_dates = [], all_time = false) => {
   const lt_years = ONE_YEAR_CRASH_DATE.includes(customer_id) ? 5 : DEFAULT_DATE_RANGE_LONG_TERM_YEARS
   const has_severe_crashes = customer_id && HAS_SEVERE_CRASHES.includes(customer_id)

    return {
    customer_id,
    tod_params: {
        has_monday: inputs.has_monday,
        has_tuesday: inputs.has_tuesday,
        has_wednesday: inputs.has_wednesday,
        has_thursday: inputs.has_thursday,
        has_friday: inputs.has_friday,
        has_saturday: inputs.has_saturday,
        has_sunday: inputs.has_sunday,
        local_start_time: inputs.start_time,
        local_end_time: inputs.end_time,
        timezone_iana: inputs.timezone_iana,
    },
    date_range: {
        start_date: dayjs(start_date).format('YYYY-MM-DD'),
        end_date: dayjs(end_date).format('YYYY-MM-DD'),
        exclude_holidays: inputs.exclude_holidays,
        timezone_iana: inputs.timezone_iana,
        period_days: inputs.period_days,
        excluded_dates,
    },
    date_range_long_term: {
        start_date: dayjs(end_date).subtract(lt_years, 'year').format('YYYY-MM-DD'),
        end_date: dayjs(end_date).format('YYYY-MM-DD'),
        exclude_holidays: inputs.exclude_holidays,
        timezone_iana: inputs.timezone_iana,
        period_days: dayjs(end_date).diff(dayjs(end_date).subtract(3, 'year'), 'days'),
        excluded_dates,
    },
    all_time,
    has_severe_crashes,
};}


export const getLatestMetricsDate = async ({ network_id, customer_id }) => {
	const [
		enabled_network_data_injection_res,
		enabled_customer_data_injection_res,
	] = await Promise.all([
		network_id != null ? client(`/api/v1/enabled-injection/network/${network_id}`) : undefined,
		client(`/api/v1/enabled-injection/${customer_id}`),
	]);

    const network_injected_date = enabled_network_data_injection_res?.latest_metrics_date;
    const is_network_data_injected = enabled_network_data_injection_res?.is_data_injected === true;
    const customer_injected_date = enabled_customer_data_injection_res?.latest_metrics_date;
    const is_customer_data_injected = enabled_customer_data_injection_res?.is_data_injected === true;

    let latest_metrics_date_sigops;
    let latest_metrics_date_non_sigops;

	let raw_network_latest_metrics_date_sigops;
	let raw_network_latest_metrics_date_sigops_hourly;

    if (network_id) {
      const [network_latest_res, network_hourly_res] = await Promise.all([
        client(`/api/v1/latest/${network_id}`),
        client(`/api/v1/latest-hourly/${network_id}`)
      ]);

      raw_network_latest_metrics_date_sigops = network_latest_res;
      raw_network_latest_metrics_date_sigops_hourly = network_hourly_res?.start_date_local;

      if (is_network_data_injected) {
        latest_metrics_date_sigops = network_injected_date;
      }
	  else if (network_hourly_res?.start_date_local) {
        latest_metrics_date_sigops = network_hourly_res.start_date_local;
      }
	  else {
        latest_metrics_date_sigops = network_latest_res;
      }
    }

    if (customer_id) {
      const [customer_latest_res, customer_hourly_res] = await Promise.all([
        client(`/api/v1/latest-customer/${customer_id}`),
        client(`/api/v1/latest-customer-hourly/${customer_id}`)
      ]);

      const customer_latest_metrics_date = is_customer_data_injected ? customer_injected_date
        : customer_hourly_res?.start_date_local || customer_latest_res

      latest_metrics_date_non_sigops = customer_latest_metrics_date;

      if (!latest_metrics_date_sigops) {
        latest_metrics_date_sigops = customer_latest_res; // To match with Sidebar Cerberus, since SigOps does not have Abacus above Network level
      }
    }

    return {
      latest_metrics_date_sigops,
      latest_metrics_date_non_sigops,
      is_metric_data_injected: is_network_data_injected || is_customer_data_injected,
	  raw_network_latest_metrics_date_sigops,
	  raw_network_latest_metrics_date_sigops_hourly,
    };
  };
